// Services
import breedService from "@/app/services/main/breed/breed-service.js";
import damSireService from "@/app/services/main/dam-sire/dam-sire-service.js";

// Utils
import { vuelidateUtil } from "@/utils/vuelidate-util.js";

// Mixins
// import { GoogleAnalyticsMixins } from '@/app/mixins/utils/google-analytics-mixins';
import { dateMixins } from "@/app/mixins/utils/date-mixins";
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { imageCompressorMixins } from "@/app/mixins/utils/image-compressor-mixins";
import { inputTextMixins } from "@/app/mixins/utils/input-text-mixins";
import { userMixins } from "@/app/mixins/modules/user-mixins.js";
import { errorFocusMixins } from "@//app/mixins/utils/error-focus-mixins.js";

// Components
const FileUpload = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "file-upload" */ "@/app/views/main-view/_shared/_components/file-upload/file-upload.vue"
    );

// Third Party libraries
import DatePicker from "vue2-datepicker";

// Constants
import { FILE_TYPE } from "@/app/constants/type-constants.js";
import { STATUS_CODES, SUCCESS_MSG } from "@/app/constants/status-constants";
import { BREEDER_ROUTES } from '@/app/constants/breeder-routes-constants';
import { DAM_SIRE_DOCUMENT } from "@/app/constants/dam-sire-documents-constants";

const DOG_GENDER = {
    DAM: "Dam",
    SIRE: "Sire",
}

export default {
    name: "AddDamSireContainer",
    components: {
        FileUpload,
        DatePicker,
    },
    mixins: [
        userMixins,
        imageCompressorMixins,
        inputTextMixins,
        toastMessageMixins,
        dateMixins,
        errorFocusMixins,
    ],
    data() {
        return {
            imageTypeList: ['image/svg','image/png','image/jpg','image/jpeg','image/gif','image/raw'],
            isAPILoading: false,
            isAPISubmitLoading: false,
            isAddingSuccess: false,
            imageToCrop: "",

            breedList: [],
            genderList: [DOG_GENDER.DAM, DOG_GENDER.SIRE],

            dogImageUploadPreviewSource: "",
            dogImageToCrop: "",
            dogImageUpload: "",

            inputFields: {
                dogName: "",
                breed: "",
                gender: "",
                dateOfBirth: "",
            },

            previousWhelpingDates: [],
            dogNotes: "",
            dogNotesHistory: [],

            documents: [],
            ALLOWED_TYPES: DAM_SIRE_DOCUMENT.ALLOWED_TYPES,
            maxDocumentsLimit: 6,
        };
    },
    computed: {
        validationRules() {
            let fields = {
                inputFields: {
                    dogName: {},
                    breed: {},
                    gender: {},
                    dateOfBirth: {},
                },
            };

            fields.inputFields = vuelidateUtil.setFieldsRequired(fields.inputFields, true);

            return fields;
        },
        dogImageSource() {
            let img = "";

            if (this.dogImageUploadPreviewSource) {
                img = this.dogImageUploadPreviewSource;
            } else {
                img = require("@/assets/images/_shared/default-profile-pic.png");
            }

            return img;
        },
        isGenderDam(){
            return this.inputFields.gender === DOG_GENDER.DAM;
        },
    },
    watch: {},
    async created() {
        await this.getBreedList();
    },
    methods: {
        async onSubmitDetails() {
            this.$v.$touch();

			if (this.$v.$invalid) {
				this.setFocusOnFirstErrorField();
                return;
			} 

            // remove added whelping datepickers with null values
            this.previousWhelpingDates = this.previousWhelpingDates.filter(({date}) => !!date);

            // format dates to properly save them (not index 0)
            const formattedDates = this.previousWhelpingDates.map(({date}) => this.dateToISO(date));

            // only add the previous whelping dates if the dog gender is a "Dam"
            const whelpingDates = this.inputFields.gender === DOG_GENDER.DAM ? formattedDates : []

            try {
                this.isAPISubmitLoading = true;

                const dateOfBirth = this.dateToISO(this.inputFields.dateOfBirth);
                const dogImageUploadSource = this.dogImageUpload && this.dogImageUpload.length ? this.dogImageUpload[0] : null;

                const damSireDetails = {
                    formData: {
                        ...this.inputFields,
                        dateOfBirth,
                        previousWhelpingDates: whelpingDates,
                        dogNotes: this.dogNotes,
                    },
                    dogImageUpload: dogImageUploadSource,
                };

                const response = await damSireService.addDamSire(damSireDetails);

                const { SUCCESS_ADD_DAM_SIRE, ERROR_ADD_DAM_SIRE } = this.toastMessages.damSire();

                if (response.status === STATUS_CODES.SUCCESS_POST) {
                    this.showCustomSuccessToast(SUCCESS_MSG.SUCCESS_TITLE, SUCCESS_ADD_DAM_SIRE);
                    this.isAddingSuccess = true;
                    this.$router.push(BREEDER_ROUTES.DAM_SIRE_LIST)

                    const { damSireId } = response.data;

                    // uploading of documents
                    const documentsToUpload = this.documents.map(document => document.file);
                    const uploadDetails = { formData: { damSireId}, documents: documentsToUpload };
                    const uploadResponse = await damSireService.uploadMultipleDamSireDocuments(uploadDetails);
                } else {
                    this.showAPIErrorToast(ERROR_ADD_DAM_SIRE, response);
                }
                
            } catch (err) {
                this.isAddingSuccess = false;
                this.showGenericErrorToast();
            } finally {
                this.isAPISubmitLoading = false;
            }
        },
        addWhelpingDate() {
            const newWhelpingDate = {
                id: Math.random().toString(),
                date: null,
            };

            this.previousWhelpingDates.push(newWhelpingDate);
        },
        removeWhelpingDate(idToRemove) {
            this.previousWhelpingDates = this.previousWhelpingDates.filter(
                (dateObj) => dateObj.id !== idToRemove
            );
        },
        sortAlphabetically(items) {
            const sortedItems = items.sort(function (a, b) {
                return a.name.localeCompare(b.name);
            });
            return sortedItems;
        },
        async getBreedList() {
            try {
                this.isAPILoading = true;
                const breedsResponse = await breedService.getBreeds();

                if (breedsResponse.status === STATUS_CODES.SUCCESS) {
                    const otherBreedOptions = breedsResponse.data.data.slice(
                        0,
                        4
                    );
                    const breeds = this.sortAlphabetically(
                        breedsResponse.data.data.slice(
                            4,
                            breedsResponse.data.count
                        )
                    );

                    // remove "Breed not specified", it has id = 0
                    this.breedList = [...otherBreedOptions, ...breeds].filter(
                        (breed) => breed.id != 0
                    );
                } else {
                    const { ERROR_GET_BREED_LIST } = this.toastMessages.breed();
                    this.showAPIErrorToast(
                        ERROR_GET_BREED_LIST,
                        breedsResponse
                    );
                }
            } catch (err) {
                this.showGenericErrorToast();
            } finally{
                this.isAPILoading = false;
            }
        },
        setImageCrop({ image, blob }) {
            this.dogImageUploadPreviewSource = image;
            this.dogImageToCrop = blob;
            this.dogImageUpload = blob;
        },
        async onUploadImage(event) {
            // let valid = await this.validateImage(event.target.files[0]);
            let valid = await this.validateImageWithType(event.target.files[0], this.imageTypeList);

            // do nothing when user cancels Upload
            if (!valid) {
                // reset uploader
                this.$refs.imgUploader.value = "";
                return;
            }

            // compress image if more than 2mb
            const file = await this.compressImage(event.target.files[0]);

            this.dogImageToCrop = [file];

            // show preview of pet image
            let reader = new FileReader();

            reader.onload = () => {
                this.imageToCrop = reader.result;
                this.$refs.modalFileUpload.show();
            };

            // this.sendEventTrackingToGA(GA.REGISTRATION_PAGE.ADD_PET_IMAGE.ACTION, GA.REGISTRATION_PAGE.CATEGORY, GA.REGISTRATION_PAGE.ADD_PET_IMAGE.LABEL);
            reader.readAsDataURL(file);
        },
        clearFileInput(event) {
            event.target.value = "";
        },
        notAfterToday(date) {
            return date > new Date();
        },
        redirectToDamSireList(){
			this.$router.push(`${BREEDER_ROUTES.DAM_SIRE_LIST}`);
        },
        onPickFile() {
            this.$refs.fileInput.click();
        },
        clearFileInput(event) {
            event.target.value = "";
        },
        async validateFileValidity(file) {
            if (!file) {
                return false;
            }

            let errorMessage = "";
            let isValid = true;

            // by default file.size is in bytes (check if it's > 20 MB in bytes)
            if (file.size > DAM_SIRE_DOCUMENT.MAX_SIZE) {
                errorMessage = "File size must not exceed 20 MB.";
                isValid = false;
            }

            const isFileTypeValid = DAM_SIRE_DOCUMENT.ALLOWED_TYPES.includes(file.type);

            if (!isFileTypeValid) {
                errorMessage = `${file.name} has unsupported file format.`;
                isValid = false;
            }

            if (file.name && file.name.length > DAM_SIRE_DOCUMENT.MAX_NAME_LENGTH) {
                errorMessage = `File name is too long.`;
                isValid = false;
            }

            if (!isValid) {
                this.showCustomErrorToast(
                    "Unable to upload file.",
                    errorMessage
                );
            }

            return isValid;
        },
        formatSize(size) {
            if(!size) return;

            return (size / 1024).toFixed(2) + ' KB';
          },
        async onFilePicked(event) {
            const files = event.target.files;

            // return early to prevent unnecessary code execution
            if (!files || (files && !files.length)) {
                return;
            }

            // validate file if it's valid or doesn't exceed 20mb
            const isFileValid = await this.validateFileValidity(files[0]);

            if (!isFileValid) {
                return;
            }

            const file = event.target.files[0];

            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const fileType = file.name.split(".").at(-1);
                    const fileUrl = this.fileFormat(fileType);

                    this.documents.push({
                        fileName: file.name,
                        file: file,
                        url: fileUrl ? fileUrl : e.target.result,
                        isIcon: fileUrl,
                        size: file.size,
                    });
                };
                reader.readAsDataURL(file);
            }

        },
        fileFormat(type) {
            const fileTypeExtensions = {
                "doc": require("@/assets/images/file-type/word.svg"),
                "docx": require("@/assets/images/file-type/word.svg"),
                "pdf": require("@/assets/images/_shared/pdf-icon.png"),
            };

            return fileTypeExtensions[type] || null;
        },
        async onRemoveDocument(index) {
            const confirmDeleteResult = await this.$swal({
                title: "Delete Document",
                text: "Are you sure you want to delete the document?",
                showCancelButton: true,
                confirmButtonColor: "#4B8BFF",
                cancelButtonColor: "#d33",
                confirmButtonText: "Confirm",
                cancelButtonText: "Cancel",
                reverseButtons: true,
                showCloseButton: true,
            });

            if (confirmDeleteResult.isConfirmed) {
                this.documents.splice(index, 1);
            }
        },
    },
    beforeRouteLeave(to, from, next) {
        const {dogName, breed, gender, dateOfBirth} = this.inputFields;

        if (!this.isAddingSuccess && (dogName || breed ||gender || dateOfBirth || this.dogImageUploadPreviewSource || this.previousWhelpingDates.length || this.dogNotes)) {
            this.$swal({
                imageHeight: '30px',
                title: 'You have unsaved changes!',
                text: 'Are you sure you want to leave this page?',
                imageUrl: `${require('@/assets/images/_shared/dog-confirm.png')}`,
                confirmButtonColor: '#4B8BFF',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Confirm',
                showCancelButton: true,
                cancelButtonText: 'Cancel',
                reverseButtons: true
            })
                .then(result => {
                    if (result.value) {
                        next(true);
                    } else {
                        next(false);
                    }
                });
        } else {
            next(true);
        }
    },
    validations() {
        return this.validationRules;
    },
};
